import React, { useCallback, useEffect, useState } from 'react';
import { Download, FileText, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { clientGet, errorMessage, isUnauthorized, fmtShortDate, fmtMoney, invoicePdfUrl } from '../../lib/clientApi';
import { StatusBadge, EmptyState, ContactAgencyButton } from './shared';

const FILTERS = [['all', 'Toutes'], ['unpaid', 'À régler'], ['paid', 'Payées']];

export function ClientInvoices({ onUnauthorized }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await clientGet('/api/client/invoices');
      setItems(res.data.items || []);
    } catch (err) {
      if (isUnauthorized(err)) return onUnauthorized();
      setError(errorMessage(err, 'Impossible de charger vos factures'));
    } finally { setLoading(false); }
  }, [onUnauthorized]);

  useEffect(() => { load(); }, [load]);

  const visible = items.filter((i) => filter === 'all' || (filter === 'paid' ? i.paid : !i.paid));
  const due = items.filter((i) => !i.paid).reduce((s, i) => s + i.remain_to_pay, 0);

  return (
    <div className="space-y-4" data-testid="client-invoices">
      <div className="flex items-center gap-2">
        <div>
          <h1 className="font-['Space_Grotesk'] text-2xl font-semibold tracking-tight">Mes factures</h1>
          {!loading && items.length > 0 && <p className="text-base text-muted-foreground" data-testid="client-invoices-due-total">{due > 0 ? `Reste à régler : ${fmtMoney(due)}` : 'Toutes vos factures sont réglées'}</p>}
        </div>
        <Button variant="ghost" size="icon" className="ml-auto h-11 w-11" onClick={load} aria-label="Actualiser" data-testid="client-invoices-refresh"><RefreshCw className="h-4 w-4" /></Button>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1" data-testid="client-list-filter-row">
        {FILTERS.map(([id, label]) => (
          <button key={id} type="button" onClick={() => setFilter(id)} aria-pressed={filter === id} data-testid={`client-invoices-filter-${id}`}
            className={`shrink-0 h-10 px-4 rounded-full border text-base font-medium transition-colors ${filter === id ? 'bg-[hsl(var(--primary))] text-white border-transparent' : 'bg-card border-border text-muted-foreground hover:text-foreground'}`}>
            {label}
          </button>
        ))}
      </div>

      {loading && <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-24 rounded-[var(--radius)]" />)}</div>}

      {!loading && error && (
        <Card className="p-4 border-destructive/30" role="alert" data-testid="client-invoices-error">
          <p className="text-base">{error}</p>
          <Button variant="outline" className="mt-3 h-11" onClick={load} data-testid="client-invoices-retry">Réessayer</Button>
        </Card>
      )}

      {!loading && !error && visible.length === 0 && (
        <EmptyState icon={FileText} title="Aucune facture" copy="Vos factures apparaîtront ici dès qu'elles seront disponibles." action={<ContactAgencyButton />} testId="client-invoices-empty-state" />
      )}

      {!loading && visible.map((inv) => (
        <Card key={inv.id} className="p-4 shadow-sm border-border" data-testid="client-invoice-card">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-base font-semibold" data-testid="client-invoice-ref">{inv.type === 2 ? 'Avoir' : 'Facture'} {inv.ref}</p>
              <p className="text-sm text-muted-foreground mt-0.5">Émise le {fmtShortDate(inv.date)}{inv.due_date && !inv.paid ? ` · échéance ${fmtShortDate(inv.due_date)}` : ''}</p>
            </div>
            <StatusBadge state={inv.state} testId="client-invoice-status-badge" />
          </div>
          <div className="mt-3 flex items-end justify-between gap-3 flex-wrap">
            <div>
              <p className="text-xl font-semibold" data-testid="client-invoice-amount">{fmtMoney(inv.total_ttc)}</p>
              {!inv.paid && inv.remain_to_pay !== inv.total_ttc && <p className="text-sm text-muted-foreground">Reste à régler : {fmtMoney(inv.remain_to_pay)}</p>}
            </div>
            <Button asChild variant="outline" className="h-11" data-testid="client-invoice-download-pdf-button">
              <a href={invoicePdfUrl(inv.id)} target="_blank" rel="noopener noreferrer"><Download className="h-4 w-4 mr-2" /> Télécharger le PDF</a>
            </Button>
          </div>
        </Card>
      ))}
    </div>
  );
}
